/**
 * BrandWatermark — the faint brand mark on band and hero surfaces
 * (Amanda's ruling 2026-07-02: every banner carries the tenant's mark
 * as a watermark; the Hub hero treatment, generalized).
 *
 * Tenant seam: renders HubBrandMark, the app's single brand-mark
 * resolution point. Today that resolves to the Peace dove; when the
 * Era-B tenant-logo lookup lands inside that seam, every watermark
 * follows automatically (the getBrandVoicePrompt pattern: the call
 * sites never change).
 *
 * Contract: the parent band must be `relative overflow-hidden`, and the
 * band's CONTENT must sit in a `relative` wrapper so it paints above
 * the mark (the Hub hero stacking pattern). The mark is decorative
 * (aria-hidden via label="") and pointer-events-none; it renders in
 * currentColor at a low alpha passed through className. Defaults match
 * the Hub hero (cream at 5% on forest); mid-tone bands want a touch
 * more (cream at 7% on sage-400) to stay a whisper.
 *
 * ANCHORING (founder soak, 2026-07-06): the mark positions against a
 * CONTENT-WIDTH frame (max-w-[1160px], the bands' shared inner-container
 * width), not the band's own edges. On viewports up to that width the
 * frame IS the band, so nothing moves; on wide/ultra-wide screens the
 * mark tracks the content column's right boundary instead of drifting
 * to the viewport edge ("very far over"). Callers' position classes
 * (right-0 + translate-x, the hero's -right offsets) are unchanged —
 * they now read relative to the frame, so the mark sits just past the
 * content edge, overlapping the column's right boundary.
 */

import { type ReactNode } from 'react';
import { HubBrandMark } from '@/components/hub/HubBrandMark';

export interface BrandWatermarkProps {
  /** Position + color classes; the default is the Hub hero treatment. */
  className?: string;
  /**
   * How the mark sizes. 'width': the caller sets a width and the mark
   * scales from it (the Hub hero treatment). 'height': the mark spans
   * the band's full height, aspect preserved, and the caller slides it
   * half off-page with a translate (Amanda's banner ruling 2026-07-03:
   * banner-height, proportionate, never stretched).
   */
  fit?: 'width' | 'height';
}

const HERO_DEFAULT =
  '-top-8 -right-[120px] w-[300px] text-cream/[5%] md:-top-9 md:-right-[60px] md:w-[440px]';

const FIT_CLASSES: Record<NonNullable<BrandWatermarkProps['fit']>, string> = {
  width: '[&>svg]:h-auto [&>svg]:w-full',
  height: 'h-full [&>svg]:h-full [&>svg]:w-auto',
};

export function BrandWatermark({ className, fit = 'width' }: BrandWatermarkProps): ReactNode {
  return (
    // The content-width anchor frame: full-band overlay, centered inner
    // column matching the bands' inner containers, so the mark's position
    // classes resolve against the content edge (see ANCHORING above).
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      <div className="relative mx-auto h-full max-w-[1160px]">
        <HubBrandMark
          label=""
          className={
            'pointer-events-none absolute ' +
            FIT_CLASSES[fit] +
            ' ' +
            (className ?? HERO_DEFAULT)
          }
        />
      </div>
    </div>
  );
}
